import { type PublicAppSettings } from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * What the platform says about itself, with no permission at all (P23b1).
 *
 * `GET /app-settings` is `@Public()` and carries exactly the settings a
 * definition opted into publishing (`memberVisible`) — for the portal that means
 * `billing.currency` above all: the code every amount on a billing surface is
 * denominated in.
 *
 * ## Why not read it from the settings api like `ai.pricing`
 *
 * Because that read needs `settings.read`, and the currency is needed on
 * surfaces that legitimately do not have it. A `billing.read` operator opening a
 * plan must see what its direct price is quoted in; an amount with no unit
 * beside it is the defect P10.5d1 records, and refusing the whole card over a
 * permission the fact does not require would be worse. One source, reachable by
 * everyone who can be shown an amount.
 *
 * ## Shared rather than module-owned (W6)
 *
 * `Subscriptions` needs it for the price board and the plan card, `Payments`
 * (P23b2) for the order desk, and any later surface quoting money. A lookup-like
 * read three modules want is exactly what `shared/services` is for.
 *
 * Cached under one tag. Deployment facts do not change under an operator, so the
 * one invalidation is a **locale switch** (`DirectionProvider`, P28): `brand.name`
 * is resolved per `Accept-Language`, and the cached payload is the old language's
 * the moment the locale changes. Since P28 the whole `brand.*` published set is
 * read through `useBrand()` rather than by key here.
 */
export const appSettingsApi = createApi({
  reducerPath: 'appSettingsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['AppSettings'],
  endpoints: (builder) => ({
    appSettings: builder.query<PublicAppSettings, void>({
      query: () => ({ url: '/app-settings', method: 'GET' }),
      providesTags: ['AppSettings'],
    }),
  }),
});

export const { useAppSettingsQuery } = appSettingsApi;
